import { useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Link, navigate } from "@reach/router";
import { FiCheckCircle, FiXCircle, FiAlertTriangle } from "react-icons/fi";
import { IconContext } from "react-icons";

import axiosObj from "libs/axios";
import Help from "components/Help";
import { ThemedButton } from "components/BuildingBlocks";
import BigSpinner from "components/BigSpinner";

import { CHECKOUT_URL } from "constants/urls";

import {
  PAYMENT_FAILED_HEADING,
  PAYMENT_FAILED_MESSAGE,
  PAYMENT_FAILURE_NAVIGATION_TEXT,
  PAYMENT_SUCCESS_HEADING,
  PAYMENT_SUCCESS_MESSAGE,
  PAYMENT_SUCCESS_NAVIGATION_TEXT,
  PAYMENT_UNKNOWN_HEADING,
  PAYMENT_UNKNOWN_MESSAGE,
  PAYMENT_UNKNOWN_NAVIGATION_TEXT,
} from "./constants";
import "./checkout-status.scss";
import { useState } from "react";

const STATUS = Object.freeze({
  PENDING: "pending",
  SUCCESS: "success",
  FAILURE: "failure",
  UNKNOWN: "unknown",
});

// TODO: Make component more generic:
// - accept link to navigate to as props or location state
// - accept navigation text as props or location state
export default function CheckoutStatus(props) {
  const [status, setStatus] = useState(STATUS.PENDING);

  const queryParams = props.location.state;

  useEffect(() => {
    if (!queryParams) {
      navigate("/");
    } else {
      const rzpResponse = queryParams.rzpResponse;

      function handleCheckoutAPISuccess(response) {
        if (response.data.status === "payment_success") {
          setStatus(STATUS.SUCCESS);
        } else {
          setStatus(STATUS.FAILURE);
        }
      }

      function handleCheckoutAPIFailure(error) {
        if (error.response && error.response.status === 422) {
          setStatus(STATUS.FAILURE);
        } else {
          setStatus(STATUS.UNKNOWN);
        }
      }

      function onFailure(rzpResponse, checkoutId) {
        const reason = rzpResponse ? rzpResponse.error.reason : "modal-close";
        axiosObj
          .patch(CHECKOUT_URL(checkoutId), {
            pgStatus: STATUS.FAILURE,
            pgError: reason,
          })
          .then(handleCheckoutAPISuccess)
          .catch(handleCheckoutAPIFailure);
      }

      function onSuccess(rzpResponse, checkoutId) {
        axiosObj
          .patch(CHECKOUT_URL(checkoutId), {
            pgStatus: STATUS.SUCCESS,
            pgPaymentId: rzpResponse.razorpay_payment_id,
            pgCheckoutId: rzpResponse.razorpay_order_id,
            pgSignature: rzpResponse.razorpay_signature,
          })
          .then(handleCheckoutAPISuccess)
          .catch(handleCheckoutAPIFailure);
      }

      if (queryParams.status === STATUS.SUCCESS) {
        onSuccess(rzpResponse, queryParams.checkoutId);
      } else {
        onFailure(rzpResponse, queryParams.checkoutId);
      }
    }
  });

  if (!queryParams) {
    return null;
  }

  let heading;
  let message;
  let navigateTo;
  let navigationText;
  let Icon;
  let iconColor;

  switch (status) {
    case STATUS.SUCCESS:
      heading = PAYMENT_SUCCESS_HEADING;
      message = PAYMENT_SUCCESS_MESSAGE;
      navigationText = PAYMENT_SUCCESS_NAVIGATION_TEXT;
      navigateTo = "/learn/course/" + queryParams.courseId + "/classes";
      Icon = <FiCheckCircle />;
      iconColor = "green";
      break;

    case STATUS.FAILURE:
      heading = PAYMENT_FAILED_HEADING;
      message = PAYMENT_FAILED_MESSAGE;
      navigationText = PAYMENT_FAILURE_NAVIGATION_TEXT;
      navigateTo = "/learn/course/" + queryParams.courseId + "/details";
      Icon = <FiXCircle />;
      iconColor = "#ff3f6c";
      break;

    case STATUS.UNKNOWN:
      heading = PAYMENT_UNKNOWN_HEADING;
      message = PAYMENT_UNKNOWN_MESSAGE;
      navigationText = PAYMENT_UNKNOWN_NAVIGATION_TEXT;
      navigateTo = "/learn/course/" + queryParams.courseId + "/details";
      Icon = <FiAlertTriangle />;
      iconColor = "#ff3f6c";
      break;

    default:
      break;
  }

  return (
    <Container className="checkout-status">
      {status !== STATUS.PENDING ? (
        <Row className="justify-content-center mt-4">
          <Col xs="auto" className="mb-4">
            <IconContext.Provider
              value={{
                color: iconColor,
                size: "14em",
              }}
            >
              {Icon}
            </IconContext.Provider>
          </Col>

          <div className="w-100"></div>
          <Col xs="auto" className="mx-auto my-2 h2">
            {heading}
          </Col>
          <div className="w-100"></div>
          <Col className="bottom-link">{message}</Col>
          <div className="w-100 mt-4"></div>
          <Col xs="auto" className="mx-auto mt-4">
            <Link to={navigateTo}>
              <ThemedButton
                size="lg"
                fontWeight={500}
                backgroundColor={status === STATUS.SUCCESS ? "green" : ""}
                hoverColor={status === STATUS.SUCCESS ? "#00b86b" : ""}
              >
                {navigationText}
              </ThemedButton>
            </Link>
          </Col>
          <div className="w-100"></div>
          <Col className="mt-4 bottom-link">
            Need help? <Help text="Reach out to us here." />
          </Col>
        </Row>
      ) : (
        <BigSpinner />
      )}
    </Container>
  );
}
